import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { format, startOfMonth, endOfMonth, startOfYear, endOfYear } from 'date-fns'
import { fr } from 'date-fns/locale'

const CATEGORIES_RECETTE = ['Honoraires', 'Autre recette']
const CATEGORIES_CHARGE = ['Loyer', 'Assurance', 'Fournitures', 'Formation', 'Abonnement logiciel', 'Transport', 'Autre charge']

export default function Comptabilite() {
  const { praticien } = useAuth()
  const [ecritures, setEcritures] = useState([])
  const [loading, setLoading] = useState(true)
  const [panel, setPanel] = useState(null)
  const [form, setForm] = useState(emptyForm())
  const [saving, setSaving] = useState(false)
  const [mois, setMois] = useState(new Date())
  const [vue, setVue] = useState('mois') // 'mois' | 'annee'

  function emptyForm() {
    return { date_ecriture: new Date().toISOString().slice(0,10), libelle: '', type: 'recette', categorie: 'Honoraires', montant: '' }
  }

  const load = useCallback(async () => {
    setLoading(true)
    // [COMPTA-DATES v1] bornes en date LOCALE via format() — toISOString() convertit en UTC
    // et décalait le début d'un jour (UTC+1/+2), faisant remonter le dernier jour du mois précédent
    const debut = format(vue === 'annee' ? startOfYear(mois) : startOfMonth(mois), 'yyyy-MM-dd')
    const fin = format(vue === 'annee' ? endOfYear(mois) : endOfMonth(mois), 'yyyy-MM-dd')
    const { data: { session } } = await supabase.auth.getSession()
    if (!session?.user?.id) { setLoading(false); return }
    const { data } = await supabase
      .from('ecritures_comptables')
      .select('*')
      .eq('praticien_id', session.user.id)
      .gte('date_ecriture', debut)
      .lte('date_ecriture', fin)
      .order('date_ecriture', { ascending: false })
    setEcritures(data || [])
    setLoading(false)
  }, [mois, vue])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    const handler = () => { setForm(emptyForm()); setPanel('new') }
    window.addEventListener('cabinet:new', handler)
    return () => window.removeEventListener('cabinet:new', handler)
  }, [])

  async function save() {
    setSaving(true)
    const payload = {
      praticien_id: praticien.id,
      date_ecriture: form.date_ecriture,
      libelle: form.libelle,
      type: form.type,
      categorie: form.categorie,
      montant: parseFloat(form.montant)
    }
    if (panel === 'new') await supabase.from('ecritures_comptables').insert(payload)
    else await supabase.from('ecritures_comptables').update(payload).eq('id', panel.id)
    setSaving(false)
    setPanel(null)
    load()
  }

  async function deleteEcriture(id) {
    if (!window.confirm('Supprimer cette écriture ?')) return
    await supabase.from('ecritures_comptables').delete().eq('id', id)
    load()
  }

  function exportCSV() {
    // Exclure les consultations gratuites (montant = 0)
    const data = ecritures.filter(e => !(e.type === 'recette' && e.montant === 0))
    const header = 'Date;Libelle;Type;Categorie;Mode;Montant\n'
    const MODE_LABEL = { cb: 'CB', cheque: 'Chèque', especes: 'Espèces', virement: 'Virement' }
    const rows = data.map(e =>
      `${e.date_ecriture};${e.libelle};${e.type === 'recette' ? 'Recette' : 'Charge'};${e.categorie || ''};${MODE_LABEL[e.mode_paiement] || e.mode_paiement || ''};${e.montant}`
    ).join('\n')
    const blob = new Blob([header + rows], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `comptabilite_${format(mois, vue === 'annee' ? 'yyyy' : 'yyyy-MM')}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const recettes = ecritures.filter(e => e.type === 'recette').reduce((s, e) => s + e.montant, 0)
  const charges = ecritures.filter(e => e.type === 'charge').reduce((s, e) => s + e.montant, 0)
  const resultat = recettes - charges

  return (
    <>
      <div style={{display:'flex', alignItems:'center', gap:10, marginBottom:20, flexWrap:'wrap'}}>
        {/* Toggle mois/année */}
        <div style={{display:'flex', border:'0.5px solid var(--border-md)', borderRadius:'var(--radius)', overflow:'hidden'}}>
          <button className="btn btn-sm" style={{borderRadius:0, border:'none', background: vue==='mois' ? 'var(--text)' : 'transparent', color: vue==='mois' ? 'var(--surface)' : 'var(--text)'}} onClick={() => setVue('mois')}>Mois</button>
          <button className="btn btn-sm" style={{borderRadius:0, border:'none', background: vue==='annee' ? 'var(--text)' : 'transparent', color: vue==='annee' ? 'var(--surface)' : 'var(--text)'}} onClick={() => setVue('annee')}>Année</button>
        </div>

        <button className="btn btn-sm" onClick={() => {
          if (vue === 'mois') setMois(new Date(mois.getFullYear(), mois.getMonth() - 1))
          else setMois(new Date(mois.getFullYear() - 1, 0))
        }}>‹</button>
        <span style={{fontWeight:500, minWidth:130, textAlign:'center'}}>
          {vue === 'annee' ? mois.getFullYear() : format(mois, 'MMMM yyyy', {locale: fr})}
        </span>
        <button className="btn btn-sm" onClick={() => {
          if (vue === 'mois') setMois(new Date(mois.getFullYear(), mois.getMonth() + 1))
          else setMois(new Date(mois.getFullYear() + 1, 0))
        }}>›</button>

        <div style={{marginLeft:'auto'}}>
          <button className="btn btn-sm" onClick={exportCSV} title="Export CSV (hors consultations gratuites)">
            ↓ Export CSV
          </button>
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card"><div className="label">Recettes</div><div className="value" style={{color:'var(--green-text)'}}>{recettes.toFixed(0)} €</div></div>
        <div className="stat-card"><div className="label">Charges</div><div className="value" style={{color:'var(--red-text)'}}>{charges.toFixed(0)} €</div></div>
        <div className="stat-card">
          <div className="label">Résultat net</div>
          <div className="value" style={{color: resultat >= 0 ? 'var(--green-text)' : 'var(--red-text)'}}>
            {resultat >= 0 ? '+' : ''}{resultat.toFixed(0)} €
          </div>
        </div>
        <div className="stat-card"><div className="label">Écritures</div><div className="value">{ecritures.filter(e => !(e.type==='recette' && e.montant===0)).length}</div></div>
      </div>

      <div className="section-header">
        <span className="section-title">Mouvements</span>
      </div>

      {loading ? <div className="empty">Chargement…</div> : (
        <div className="table-wrap">
          {ecritures.length === 0 && <div className="empty" style={{padding:32}}>Aucune écriture</div>}
          {ecritures.map(e => (
            <div key={e.id} className="compta-row">
              <div>
                <div style={{display:'flex', alignItems:'center', gap:8}}>
                  <span style={{fontSize:13, fontWeight:500}}>{e.libelle}</span>
                  {e.source === 'auto' && (
                    <span style={{fontSize:10, padding:'1px 6px', borderRadius:10, background:'var(--blue-bg)', color:'var(--blue-text)', fontWeight:500}}>
                      auto
                    </span>
                  )}
                </div>
                <div style={{fontSize:12, color:'var(--text-3)', marginTop:2}}>
                  {format(new Date(e.date_ecriture), 'dd/MM/yyyy')} · {e.categorie}{e.mode_paiement ? ' · ' + ({cb:'CB',cheque:'Chèque',especes:'Espèces',virement:'Virement'}[e.mode_paiement] || e.mode_paiement) : ''}
                </div>
              </div>
              <div style={{display:'flex', alignItems:'center', gap:12}}>
                <span className={e.type === 'recette' ? 'compta-credit' : 'compta-debit'}>
                  {e.type === 'recette' ? '+' : '-'}{e.montant} €
                </span>
                {e.source !== 'auto' && (
                  <>
                    <button className="btn btn-sm" onClick={() => { setForm({...e}); setPanel(e) }}>✎</button>
                    <button className="btn btn-sm btn-danger" onClick={() => deleteEcriture(e.id)}>✕</button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {panel && (
        <>
          <div className="panel-overlay" onClick={() => setPanel(null)} />
          <div className="panel">
            <div className="panel-header">
              <h3>{panel === 'new' ? 'Nouvelle écriture' : 'Modifier l\'écriture'}</h3>
              <button className="close-btn" onClick={() => setPanel(null)}>✕</button>
            </div>
            <div className="panel-body">
              <div className="form-row">
                <div className="form-group">
                  <label>Type</label>
                  <select value={form.type} onChange={e => setForm({...form, type: e.target.value, categorie: e.target.value === 'recette' ? 'Honoraires' : 'Loyer'})}>
                    <option value="recette">Recette</option>
                    <option value="charge">Charge</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Date</label>
                  <input type="date" value={form.date_ecriture} onChange={e => setForm({...form, date_ecriture: e.target.value})} />
                </div>
              </div>
              <div className="form-group" style={{marginBottom:14}}>
                <label>Libellé *</label>
                <input value={form.libelle} onChange={e => setForm({...form, libelle: e.target.value})} placeholder="Description de l'écriture" />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Catégorie</label>
                  <select value={form.categorie} onChange={e => setForm({...form, categorie: e.target.value})}>
                    {(form.type === 'recette' ? CATEGORIES_RECETTE : CATEGORIES_CHARGE).map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Montant (€) *</label>
                  <input type="number" step="0.01" value={form.montant} onChange={e => setForm({...form, montant: e.target.value})} />
                </div>
              </div>
            </div>
            <div className="panel-footer">
              <button className="btn btn-sm" onClick={() => setPanel(null)}>Annuler</button>
              <button className="btn btn-primary btn-sm" onClick={save} disabled={saving || !form.libelle || !form.montant}>
                {saving ? 'Enregistrement…' : panel === 'new' ? 'Ajouter' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </>
      )}
    </>
  )
}
